import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, XCircle } from "lucide-react";
import { api, apiErrorMessage } from "../api";
import { useAuth } from "../context/AuthContext";

// Module-level guard: survives React 18 StrictMode remounts so we never
// consume a single-use SSO token twice.
const inFlight = new Map(); // token -> Promise<AuthOut>

export default function SsoLanding() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { establishSession } = useAuth();
  const [state, setState] = useState({ status: "loading", message: "Signing you in via The Bench…" });

  useEffect(() => {
    const token = params.get("token");
    const redirect = params.get("redirect");
    if (!token) {
      setState({ status: "error", message: "Missing SSO token. This link is invalid or has expired." });
      return;
    }

    // De-dupe: if this token exchange is already in-flight (StrictMode remount, quick nav),
    // await the same promise instead of firing a second network call.
    // We deliberately keep the promise in memory for the tab's lifetime — the token is
    // single-use and short-lived anyway, so this doesn't leak meaningfully.
    let promise = inFlight.get(token);
    if (!promise) {
      // Clear any prior session so we land as the SSO user
      localStorage.removeItem("fb_token");
      localStorage.removeItem("fb_workspace_id");
      promise = api.sso(token);
      inFlight.set(token, promise);
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await promise;
        if (cancelled) return;
        establishSession(data);
        const dest = redirect
          || (["readonly", "support", "admin"].includes(data.user?.platform_role) ? "/staff" : "/app");
        setState({ status: "ok", message: `Welcome, ${data.user?.name || data.user?.email}.` });
        setTimeout(() => nav(dest, { replace: true }), 400);
      } catch (ex) {
        if (cancelled) return;
        const detail = apiErrorMessage(ex, "SSO failed");
        setState({ status: "error", message: detail });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-forest text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-4" data-testid="sso-landing">
        <div className="inline-flex items-center gap-2 text-coral">
          <ShieldCheck size={22} />
          <span className="text-xs uppercase tracking-wide">The Bench → Full Bench</span>
        </div>
        <h1 className="font-display text-3xl">
          {state.status === "loading" && "Handing you off…"}
          {state.status === "ok" && "You're in."}
          {state.status === "error" && "Sign-in failed"}
        </h1>
        <div className="min-h-[64px] flex items-center justify-center">
          {state.status === "loading" && (
            <div className="animate-pulse text-white/80 text-sm" data-testid="sso-loading">{state.message}</div>
          )}
          {state.status === "ok" && (
            <div className="text-emerald-300 text-sm" data-testid="sso-ok">{state.message}</div>
          )}
          {state.status === "error" && (
            <div className="text-coral text-sm flex items-center gap-2" data-testid="sso-error">
              <XCircle size={16} /> {state.message}
            </div>
          )}
        </div>
        {state.status === "error" && (
          <div className="text-xs text-white/60">
            Head back to <Link to="/" className="text-coral underline">The Bench</Link> and retry, or{" "}
            <Link to="/signin" className="text-coral underline">sign in manually</Link>.
          </div>
        )}
      </div>
    </div>
  );
}
